//frontend/src/pages/SelectService.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
   Container, 
   Typography, 
   Grid,
   Box,
   CircularProgress,
   Paper,
   Menu,
   MenuItem,
   Button,
   Tabs,
   Tab
} from '@mui/material';
import { 
    AccessTime as AccessTimeIcon, 
    RadioButtonUnchecked, 
    RadioButtonChecked,
    KeyboardArrowDown 
} from '@mui/icons-material';
import ApiIcon from '@mui/icons-material/Api';
import { useBooking } from '../context/BookingContext';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../hooks/useSettings';

export default function SelectService() {
   const { t } = useTranslation();
   const { formatPrice } = useSettings();
   const [services, setServices] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const { setBookingData, setActiveStep } = useBooking();
   const navigate = useNavigate();
   const [selectedCategory, setSelectedCategory] = useState('all');
   const [categories, setCategories] = useState(['all']);
   const [anchorEl, setAnchorEl] = useState(null);
   const [useDropdown, setUseDropdown] = useState(false);
   const [selectedServiceId, setSelectedServiceId] = useState(null);
   const tabsRef = useRef(null);

   useEffect(() => {
       fetchServices();
       setActiveStep(0);
   }, []);

   useEffect(() => {
       if (services.length > 0) {
           const uniqueCategories = ['all', ...new Set(services.map(s => s.category).filter(Boolean))];
           setCategories(uniqueCategories);
           checkWidth();
       }
       window.addEventListener('resize', checkWidth);
       return () => window.removeEventListener('resize', checkWidth);
   }, [services]);

   const checkWidth = () => {
       if (tabsRef.current) {
           const tabsWidth = categories.length * 120;
           const containerWidth = tabsRef.current.offsetWidth;
           setUseDropdown(tabsWidth > containerWidth);
       }
   };

   const fetchServices = async () => {
       try {
           const response = await axios.get('https://crm.aunimeda.com/services');
           setServices(response.data);
       } catch (error) {
           console.error('Error fetching services:', error);
           setError('Failed to load services. Please try again later.');
       } finally {
           setLoading(false);
       }
   };

   const handleServiceSelect = (service) => {
       setSelectedServiceId(service._id);
       setBookingData(prev => ({
           ...prev,
           service: service
       }));
       
       setTimeout(() => {
           setActiveStep(1);
           navigate('/select-specialist');
       }, 150); // небольшая задержка чтобы было видно эффект выбора
   };

   const handleClick = (event) => {
       setAnchorEl(event.currentTarget);
   };

   const handleClose = (category) => {
       if (typeof category === 'string') {
           setSelectedCategory(category);
       }
       setAnchorEl(null);
   };

   const filteredServices = services.filter(service => 
       selectedCategory === 'all' || service.category === selectedCategory
   );

   if (loading) return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Container>;
   if (error) return <Container><Typography color="error" align="center" sx={{ mt: 4 }}>{error}</Typography></Container>;

   return (
       <Container maxWidth="md">
           <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
               {t('booking.steps.selectService')}
           </Typography>

           <Box ref={tabsRef} sx={{ mb: 4 }}>
               {useDropdown ? (
                   <Button
                       onClick={handleClick}
                       endIcon={<KeyboardArrowDown />}
                       variant="outlined"
                       sx={{
                           borderRadius: 2,
                           textTransform: 'none',
                           minWidth: 200,
                           justifyContent: 'space-between',
                           px: 2,
                           py: 1
                       }}
                   >
                       {selectedCategory === 'all' ? t('common.allServices') : selectedCategory}
                   </Button>
               ) : (
                   <Tabs 
                       value={categories.indexOf(selectedCategory)}
                       onChange={(_, index) => setSelectedCategory(categories[index])}
                       variant="scrollable"
                       scrollButtons="auto"
                       sx={{
                           borderBottom: 1,
                           borderColor: 'divider'
                       }}
                   >
                       {categories.map((category) => (
                           <Tab 
                               key={category}
                               label={category === 'all' ? t('common.allServices') : category}
                               sx={{ 
                                   textTransform: 'none',
                                   '&.Mui-selected': {
                                       color: 'primary.main',
                                       fontWeight: 'bold'
                                   }
                               }}
                           />
                       ))}
                   </Tabs>
               )}

               <Menu
                   anchorEl={anchorEl}
                   open={Boolean(anchorEl)}
                   onClose={() => handleClose()}
                   PaperProps={{
                       elevation: 3,
                       sx: {
                           mt: 1,
                           minWidth: 200,
                           maxHeight: 300,
                           borderRadius: 2
                       }
                   }}
               >
                   {categories.map((category) => (
                       <MenuItem
                           key={category}
                           onClick={() => handleClose(category)}
                           selected={category === selectedCategory}
                           sx={{
                               py: 1.5,
                               px: 2,
                               '&.Mui-selected': {
                                   backgroundColor: 'primary.light',
                                   '&:hover': {
                                       backgroundColor: 'primary.light'
                                   }
                               }
                           }}
                       >
                           {category === 'all' ? t('common.allServices') : category}
                       </MenuItem>
                   ))}
               </Menu>
           </Box>

           <Grid container spacing={2}>
               {filteredServices.map((service) => (
                   <Grid item xs={12} key={service._id}>
                       <Paper
                           onClick={() => handleServiceSelect(service)}
                           sx={{
                               p: 2,
                               cursor: 'pointer',
                               display: 'flex',
                               alignItems: 'center',
                               '&:hover': { 
                                   bgcolor: 'rgba(0,0,0,0.02)',
                                   transform: 'translateX(8px)',
                                   transition: 'all 0.2s'
                               }
                           }}
                       >
                           <Box sx={{ 
                               width: 48,
                               height: 48,
                               bgcolor: 'primary.light',
                               borderRadius: '12px',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               mr: 2.5
                           }}>
                               <ApiIcon color="primary" />
                           </Box>

                           <Box sx={{ flex: 1 }}>
                               <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
                                   {service.name}
                               </Typography>
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                       <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                       <Typography variant="body2" color="text.secondary">
                                           {service.duration} {t('common.minutes')}
                                       </Typography>
                                   </Box>
                                   <Typography variant="subtitle1" color="primary.main" fontWeight={500}>
                                       {service.maxPrice ? 
                                           `${formatPrice(service.price)} - ${formatPrice(service.maxPrice)}` : 
                                           formatPrice(service.price)}
                                   </Typography>
                               </Box>
                           </Box>
                           {service._id === selectedServiceId ? 
                               <RadioButtonChecked sx={{ color: 'primary.main' }} /> :
                               <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />
                           }
                       </Paper>
                   </Grid>
               ))}
           </Grid>
       </Container>
   );
}